import { Alert, Box, Stack, Typography } from '@mui/material'
import Container from '@mui/material/Container'
import { CreateCampaignHeader } from '~/features/campaign_create/Header'
import { CreateCampaignContext } from '~/contexts/CreateCampaignContext'
import { FC, useContext } from 'react'
import { CampaignCategory, CampaignCostHandlingMethod, ExpenseEvidenceType, SearchGenre } from '~/generated/graphql'
import { updateTagsWithDefaults } from '~/features/campaign_input/updateTagsWithDefaults'
import { Guidance, guidanceSchemaFields, validateGuidance } from '~/features/campaign_input/Guidance'
import { OfferAutoMessage, offerAutoMessageSchemaFields } from '~/features/campaign_input/OfferAutoMessage'
import { createOfferAutoMessage } from '../form_create/CreateOfferAutoMessage'
import { NavigationButtons } from '../NavigationButtons'
import { useCampaignPageStepForm } from '../useCampaignPageStepForm'
import { FormProvider } from 'react-hook-form'
import { z } from 'zod'
import { AgeLimit, ageLimitSchemaFields } from '~/features/campaign_input/AgeLimit'
import { CAMPAIGN_FORM_TITLE, GUIDANCE_HELPER_TEXT } from '~/constants/campaign'
import InfoIcon from '@mui/icons-material/Info'
import WarningIcon from '@mui/icons-material/Warning'
import { CampaignPostFooter } from '~/components/CampaignPostFooter'
import { colorPalette } from '~/constants/styles/colors'
import {
  DesiredLanguages,
  desiredLanguagesSchemaFields,
  validateDesiredLanguages,
} from '~/features/campaign_input/DesiredLanguages'

const schema = z
  .object({
    ...ageLimitSchemaFields,
    ...guidanceSchemaFields,
    ...offerAutoMessageSchemaFields,
    ...desiredLanguagesSchemaFields,
  })
  .superRefine(validateGuidance)
  .superRefine(validateDesiredLanguages)

export const InfluencerContactInfo: FC = () => {
  const { campaign, setCampaign } = useContext(CreateCampaignContext)
  const category = campaign.category
  const costHandlingMethod = campaign.costHandlingMethod

  const { onSubmit, onBack, useFormMethods } = useCampaignPageStepForm({
    defaultValues: {
      ageLimit: campaign.ageLimit,
      guidance: campaign.guidance,
      offerAutoMessage:
        campaign.offerAutoMessage ||
        createOfferAutoMessage({
          category: category,
          costHandlingMethod: costHandlingMethod,
        }),
      category: category,
      costHandlingMethod: campaign.costHandlingMethod,
      desiredLanguages: campaign.desiredLanguages,
    },
    schema,
    commit: (data: z.output<typeof schema>) => {
      setCampaign((campaign) => {
        return {
          ...campaign,
          ...data,
          snsRequirements: updateTagsWithDefaults(
            campaign.snsRequirements,
            campaign.category ?? data.category,
            campaign.searchSubGenreIds,
            data.desiredLanguages,
          ),
        }
      })
    },
  })

  const isServiceBeauty = category === CampaignCategory.Service && campaign.searchGenre === SearchGenre.Beauty
  const isServiceGourmet = category === CampaignCategory.Service && campaign.searchGenre === SearchGenre.Gourmet
  const isOrderNumber = campaign.expenseEvidenceType === ExpenseEvidenceType.OrderNumber

  return (
    <form onSubmit={onSubmit}>
      <CreateCampaignHeader title="応募条件・案内文に関する情報について教えてください" onBack={onBack} />
      <Container sx={{ display: 'flex', flexDirection: 'column' }} maxWidth="sm">
        <FormProvider {...useFormMethods}>
          <Stack gap={5}>
            <Stack>
              <Typography variant="headingM">{CAMPAIGN_FORM_TITLE.applicationConditions}</Typography>
              <AgeLimit />
            </Stack>
            <DesiredLanguages
              description={
                <Stack gap={1} mb={1}>
                  <Alert severity="info">日本語以外の言語を設定すると、海外からの観光客を集客しやすくなります！</Alert>
                  <Typography variant="body2">
                    ここで選択した言語に応じて、次のページの「インフルエンサーの投稿に載せるハッシュタグ」で言語別に指定できます。
                  </Typography>
                </Stack>
              }
            />
            <Guidance
              description={
                campaign.costHandlingMethod === CampaignCostHandlingMethod.NoChargeCampaign ? (
                  <GuidanceNoChangeCampaignDescription
                    isServiceBeauty={isServiceBeauty}
                    isServiceGourmet={isServiceGourmet}
                  />
                ) : campaign.category === CampaignCategory.Service ? (
                  <GuidanceServiceDescription />
                ) : (
                  <GuidanceProductDescription isOrderNumber={isOrderNumber} />
                )
              }
              tooltip={
                campaign.costHandlingMethod === CampaignCostHandlingMethod.NoChargeCampaign ? (
                  <GuidanceNoChangeCampaignTooltip category={campaign.category} />
                ) : campaign.category === CampaignCategory.Service ? (
                  <GuidanceExpenseServiceTooltip />
                ) : (
                  <GuidanceExpenseProductTooltip />
                )
              }
            />
            <OfferAutoMessage
              tooltip={
                <>
                  <Alert icon={false} severity="warning">
                    ※本項目でインフルエンサーに対しての依頼を記載することはできません。
                  </Alert>
                  <Typography variant="body2" mt={2}>
                    採用時に自動で採用したインフルエンサーへ送信されるメッセージです。
                  </Typography>
                </>
              }
            />
          </Stack>
        </FormProvider>

        <NavigationButtons />
      </Container>
    </form>
  )
}

const GuidanceNoChangeCampaignDescription: FC<{
  isServiceBeauty: boolean
  isServiceGourmet: boolean
}> = ({ isServiceBeauty, isServiceGourmet }) => {
  return (
    <>
      <Box pb={1.5}>
        <Typography variant="body2">事前にインフルエンサーへ案内する内容があればご記載ください。</Typography>
        {isServiceBeauty && (
          <Alert severity="info" icon={<InfoIcon />} sx={{ my: 1 }}>
            「所要時間の目安」と「施術スタッフの性別」を記載すると、
            <br />
            応募者が内容を理解しやすくなり、応募が集まりやすくなります
          </Alert>
        )}
        {isServiceGourmet && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ my: 1 }}>
            テイクアウト商品の場合はその旨をご記載ください。
          </Alert>
        )}
      </Box>
    </>
  )
}

export const GuidanceServiceDescription: FC = () => {
  return (
    <Stack gap={2}>
      <Typography variant="body2">
        サービスを体験する際の注意点（予約が必要かどうか、来店時の注意点などをご記載ください。）
        <br />
        インフルエンサーが安心してスムーズにサービスを体験できるよう、事前に知っておくべき情報はできるだけ詳しくご記載ください。
      </Typography>

      <Stack gap={1}>
        <Typography variant="body2">文面の一例としてご活用ください。</Typography>
        <CampaignPostFooter>
          <Typography variant="body2">
            一般の方と同様にご来店ください。
            <br />
            ※繁忙期等予約が必要な場合がありますのでご確認お願いいたします。
          </Typography>
        </CampaignPostFooter>
      </Stack>
    </Stack>
  )
}

export const GuidanceProductDescription: FC<{
  isOrderNumber: boolean
}> = ({ isOrderNumber }) => {
  const { campaign } = useContext(CreateCampaignContext)

  return (
    <Stack gap={3}>
      <Stack gap={1}>
        <Typography variant="body2">
          商品の購入方法について、インフルエンサーが迷わず購入できるよう、具体的にご記載ください。
        </Typography>

        <Typography variant="body2" mt={1}>
          特定のECサイトや販売ページからの購入をお願いする場合は、そのURLと注意点などを詳しくご記載ください。
        </Typography>
        <Typography variant="body2" mt={1}>
          正確な購入方法の案内は、スムーズな体験と正しい精算処理につながります。
        </Typography>

        {campaign.category === CampaignCategory.Product &&
          campaign.costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ my: 1 }}>
              注文確定から商品到着までの目安期間を「ご案内」に記載してください。
              <br />
              記載がない場合はキャンペーン審査の進行ができませんのでご注意ください。
              <br />
              注文確定お届け日までに11日以上かかる場合はインフルエンサー立替型の利用ができません。
            </Alert>
          )}
      </Stack>

      <Stack gap={1}>
        <Typography variant="body2">文面の一例としてご活用ください。</Typography>
        <CampaignPostFooter>
          <Typography variant="body2">キャンペーンに記載されている「購入リンク」からご購入下さい。</Typography>
          {isOrderNumber && (
            <>
              <Typography variant="body2">注文番号は注文確定案内のメールに記載されています。</Typography>
              <Typography variant="body2">※●●サイトの注文番号の桁数は▲桁です。</Typography>
            </>
          )}
        </CampaignPostFooter>
      </Stack>
    </Stack>
  )
}

const GuidanceNoChangeCampaignTooltip: FC<{ category: CampaignCategory | null }> = (category) => {
  return (
    <>
      <Alert icon={false} severity="warning">
        ※本項目でインフルエンサーに対しての依頼を記載することはできません。
      </Alert>
      <Stack mt={2} gap={0.5}>
        <Typography variant="body2">記入例：</Typography>
        <CampaignPostFooter>
          <Stack gap={2}>
            {category.category === CampaignCategory.Product ? (
              <Stack gap={0.5}>
                <Typography variant="body2">・商品発送の注意点について</Typography>
                <Typography variant="body2" color={colorPalette.text.secondary} ml={3}>
                  {GUIDANCE_HELPER_TEXT[CampaignCategory.Product]}
                </Typography>
              </Stack>
            ) : (
              <Stack gap={0.5}>
                <Typography variant="body2">・服装・持ち物について</Typography>
                <Typography variant="body2" color={colorPalette.text.secondary} ml={3}>
                  {GUIDANCE_HELPER_TEXT[CampaignCategory.Service]}
                </Typography>
              </Stack>
            )}
          </Stack>
        </CampaignPostFooter>
      </Stack>
    </>
  )
}

export const GuidanceExpenseServiceTooltip: FC = () => {
  return (
    <Stack gap={2}>
      <Typography variant="body2">
        サービスを体験していただくにあたり、予約の有無や来店時の注意事項など、事前に確認すべき情報がある場合は、できるだけ詳しくご記載ください。
      </Typography>

      <Typography variant="body2">
        インフルエンサーの方が安心してスムーズにご来店・体験できるよう、下記のような情報を記載いただくことをおすすめします。
      </Typography>

      <Typography variant="body2">
        記載内容が明確であるほど、体験者との行き違いやトラブルを防ぐことができます。
        <br />
        ご協力のほど、よろしくお願いいたします
      </Typography>
    </Stack>
  )
}

export const GuidanceExpenseProductTooltip: FC = () => {
  return (
    <Stack gap={2}>
      <Typography variant="body2">
        インフルエンサーの方が迷うことなく商品を購入できるよう、具体的かつ分かりやすく購入手順をご記載ください。
      </Typography>

      <Typography variant="body2">
        特定のECサイトや販売ページからのみ購入していただきたい場合は、該当URLとともに、他サイトでは対象外である旨も必ずご記載ください。
      </Typography>

      <Typography variant="body2">
        正確なご案内は、インフルエンサーによるスムーズな商品体験と、正確な立替精算の実施につながります。
      </Typography>
    </Stack>
  )
}
